"use client";

/**
 * Route-level error boundary. Catches render/runtime crashes in any page
 * under the root layout (the layout itself is covered by global-error.tsx).
 *
 * Stale-deploy chunk failures auto-reload once via maybeAutoReload; anything
 * else (or a second failure inside the loop window) shows a styled retry
 * card instead of the raw Next.js error text.
 */

import { useEffect, useState } from "react";
import { maybeAutoReload, reportCrash } from "@/lib/crash-recovery";

export default function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    const reloaded = maybeAutoReload(error);
    setReloading(reloaded);
    reportCrash(error, { boundary: "root", digest: error?.digest, autoReloading: reloaded });
  }, [error]);

  // Mid-reload: keep the screen blank instead of flashing an error card.
  if (reloading) {
    return <div className="min-h-screen bg-zinc-950" />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950 px-6">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-900/80 p-8 text-center shadow-2xl">
        <h1 className="text-lg font-semibold text-white">Something went wrong</h1>
        <p className="mt-2 text-sm text-white/60">
          A quick refresh usually fixes this. Your work is saved.
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-zinc-950 transition hover:bg-white/90"
          >
            Refresh page
          </button>
          <button
            type="button"
            onClick={() => reset()}
            className="rounded-lg border border-white/15 px-4 py-2 text-sm font-medium text-white/80 transition hover:bg-white/5"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}
